/* eslint-disable max-lines-per-function */
/* eslint-disable complexity */
import React from "react";
import { useQuery } from "@apollo/client";
import { Grid, Typography, RadioGroup, FormControlLabel, Radio } from "@mui/material";
import { InputWithErrorLabel } from "components/haven-design/form/inputs";
import { isValidEmail } from "data/yup-validation";
import { GET_PLAN_BY_ID } from "pages/dashboard/admin/plans/queries";
import NextButton from "./next-button";
import type { plan } from "types/gql-types";
import type { CommonStepProps } from "../step-props";

type Props = Pick<CommonStepProps, "formik" | "onNext">;

const StepPayment: React.FC<Props> = ({ formik, onNext }) => {
  const planId = (formik.values.planId || "").trim();
  const { data: planData } = useQuery<plan>(GET_PLAN_BY_ID, {
    variables: { id: planId },
    skip: !planId,
    fetchPolicy: "cache-first",
  });
  const annualDiscountPercent = Number(
    planData?.plan?.defaultPricing?.annualDiscountPercent ?? 0,
  );

  const billingCycle = (formik.values.billingCycle || "annually") as "annually" | "quarterly";

  const method = (formik.values.payment?.method || "invoice") as "invoice" | "credit_card" | "ach";
  const accountingEmail = (formik.values.payment?.accountingEmail || "").trim();

  const isEmailValid = React.useMemo(() => {
    if (!accountingEmail) return false;
    return isValidEmail(accountingEmail);
  }, [accountingEmail]);

  const requireAccountingEmail = method === "invoice";
  const allowNext = !requireAccountingEmail || (accountingEmail.length > 0 && isEmailValid);

  return (
    <Grid size={{ xs: 12, md: 8, lg: 6 }} container spacing={1}>
      <Grid size={{ xs: 12 }}>
        <Typography variant="subtitle2">Billing Frequency</Typography>
        <RadioGroup
          name="billingCycle"
          value={billingCycle}
          row
          onChange={(e) => {
            const value = (e.target as HTMLInputElement).value as "annually" | "quarterly";
            formik.setFieldValue("billingCycle", value);
          }}
          style={{ marginTop: 4 }}
        >
          <FormControlLabel
            value="annually"
            control={<Radio />}
            label={(
              <span>
                Annual
                {annualDiscountPercent > 0 && (
                  <Typography
                    component="span"
                    variant="body2"
                    color="primary"
                    sx={{ ml: 1 }}
                  >
                    ({annualDiscountPercent}% discount)
                  </Typography>
                )}
              </span>
            )}
          />
          <FormControlLabel value="quarterly" control={<Radio />} label="Quarterly" />
        </RadioGroup>
      </Grid>

      <Grid size={{ xs: 12 }}>
        <Typography variant="subtitle2">Payment Method</Typography>
        <Typography variant="body2" color="textSecondary">
          You won&apos;t be charged until the contract is signed.
        </Typography>
      </Grid>

      <Grid size={{ xs: 12 }}>
        <RadioGroup
          name="payment.method"
          value={method}
          onChange={(e) => {
            const value = (e.target as HTMLInputElement).value as "invoice" | "credit_card" | "ach";
            formik.setFieldValue("payment.method", value);
          }}
        >
          <FormControlLabel value="invoice" control={<Radio />} label="Invoice" />
          {/* <FormControlLabel value="credit_card" control={<Radio />} label="Credit Card" />
          <FormControlLabel
            value="ach"
            control={<Radio />}
            label={(
              <Box display="inline-flex" alignItems="center">
                ACH
                <Tooltip title="We auto draft your bank account each month">
                  <InfoOutlined fontSize="small" style={{ marginLeft: 6 }} />
                </Tooltip>
              </Box>
            )}
          /> */}
        </RadioGroup>
      </Grid>

      {requireAccountingEmail && (
        <Grid size={{ xs: 12 }}>
          <InputWithErrorLabel
            name="payment.accountingEmail"
            label="Accounting Email"
            margin="none"
            required
          />
        </Grid>
      )}

      <Grid size={{ xs: 12 }}>
        <NextButton
          onNext={onNext}
          isValid={allowNext}
        />
      </Grid>
    </Grid>
  );
};

export default StepPayment;


